// src/pages/FormPage/FormRenderer.jsx
import React, { useState, useEffect } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { useFormLoader } from './useFormLoader';
import { runAction } from './services/runAction';
import SpaRenderer from './renderers/SpaRenderer';
import DefaultRenderer from './renderers/DefaultRenderer';
import NotFound from './renderers/NotFound';
import LoadingForm from './renderers/LoadingForm';
import ThankYou from './renderers/ThankYou';
import ErrorMessage from './renderers/ErrorMessage';
import LoginPage from '../LoginPage';
import LogoutButton from './components/LogoutButton';

/**
 * Componente que renderiza o formulário dinâmico baseado em dados remotos
 * @param {object} props
 * @param {string} props.account
 * @param {string} props.project
 * @param {string} props.formid
 * @param {string} props.token
 * @returns {JSX.Element}
 */
const FormRenderer = ({ account, project, formid, token }) => {
  const {
    formSchema,
    uiSchema,
    formData,
    config,
    customStyle,
    functions,
    spaLayout,
    schemaTY,
    isLoading,
  } = useFormLoader({ account, project, formid });

  const [submitted, setSubmitted] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [errorStatus, setErrorStatus] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (typeof config?.requiresAuth === 'boolean') {
      if (!config.requiresAuth) {
        setIsAuthenticated(true);
        setAuthChecked(true);
        return;
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAuthenticated(false);
        setAuthChecked(true);
        return;
      }

      try {
        const userDocRef = doc(firestore, `accounts/${account}/users/${user.uid}`);
        const userDoc = await getDoc(userDocRef);
        if (!userDoc.exists()) {
          console.warn('Usuário autenticado, mas não encontrado na conta indicada.');
          setErrorStatus('Você não tem permissão para acessar este formulário.');
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      } catch (e) {
        console.error('Erro ao verificar usuário na conta', e);
        setErrorStatus('Erro ao verificar permissões de acesso. Tente novamente.');
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    });

    return () => unsubscribe();
  }, [config?.requiresAuth, account]);

  useEffect(() => {
    if (customStyle) {
      const styleTag = document.createElement('style');
      styleTag.innerHTML = customStyle;
      document.head.appendChild(styleTag);
      return () => document.head.removeChild(styleTag);
    }
  }, [customStyle]);

  const onSubmit = async ({ formData }) => {
    console.log('Submitting', config);
    setDisabled(true);
    try {
      let resp = await runAction(
        config.submitAction || `wf:${config.messageName || 'newFormSubmission'}`,
        formData,
        config,
        token
      );
      setSubmitted(true);
      console.log('resp', resp);
    } catch (e) {
      setSubmitted(true);
      setErrorStatus(e.message);
      console.error('Erro ao submeter o formulário', e);
    } finally {
      setDisabled(false);
    }
  };

  const onChange = () => {};

  // Aguarda verificação de autenticação antes de qualquer render
  if (isLoading || !authChecked) return <LoadingForm />;

  // Se houve erro de permissão, exibe mensagem de erro
  if (errorStatus) {
    return <ErrorMessage message={errorStatus} />;
  }

  // Se não autenticado, envia para login
  if (config?.requiresAuth && !isAuthenticated) {
    return <LoginPage redirect={`/form/?account=${account}&project=${project}&formid=${formid}`} />;
  }

  if (formSchema === null) return <NotFound />;

  if (submitted) {
    if (errorStatus && errorStatus !== '') {
      return <ErrorMessage message={errorStatus} />;
    }
    return (
      <>
        {config?.requiresAuth && isAuthenticated && <LogoutButton />}
        <ThankYou schemaTY={schemaTY} />
      </>
    );
  }
  console.log('spaLayout', spaLayout)
  console.log('functions', functions)
  if (authChecked && isAuthenticated && spaLayout && formSchema) {
    console.log('AQUI no SPA')
    return (
      <>
        {config?.requiresAuth && isAuthenticated && <LogoutButton />}
        <SpaRenderer
          spaLayout={spaLayout}
          functions={functions}
          formSchema={formSchema}
          uiSchema={uiSchema}
          formData={formData}
          onSubmit={onSubmit}
          onChange={onChange}
          disabled={disabled}
        />
      </>
    );
  }

  if (authChecked && isAuthenticated && formSchema) {
    console.log('Fora do SPA')
    return (
      <>
        {config?.requiresAuth && isAuthenticated && <LogoutButton />}
        <DefaultRenderer
          formSchema={formSchema}
          uiSchema={uiSchema}
          formData={formData}
          onSubmit={onSubmit}
          onChange={onChange}
          disabled={disabled}
          config={config}
        />
      </>
    );
  }

  return <NotFound />;
};

export default FormRenderer;
